import { casinos } from '../data/casinos';
import { slots } from '../data/slots';
import { providers } from '../data/providers';
import type { Casino } from '../types/casino';
import type { Provider } from '../types/provider';
import type { Slot } from '../types/slot';

export function getSlotBySlug(slug: string): Slot | undefined {
  return slots.find((s) => s.slug === slug);
}

export function getSlotByRank(rank: number): Slot | undefined {
  return slots.find((s) => s.rank === rank);
}

export function getSimilarSlots(slot: Slot, count = 3): Slot[] {
  const scored = slots
    .filter((s) => s.slug !== slot.slug)
    .map((s) => ({
      s,
      w:
        (s.provider === slot.provider ? 2 : 0) +
        (s.theme === slot.theme ? 3 : 0) +
        (s.volatility === slot.volatility ? 1 : 0) +
        (s.mechanic.split(' · ')[0] === slot.mechanic.split(' · ')[0] ? 2 : 0),
    }))
    .sort((a, b) => b.w - a.w || a.s.rank - b.s.rank);
  return scored.slice(0, count).map((x) => x.s);
}

export function getCasinosForSlot(slot: Slot): Casino[] {
  return casinos
    .filter((c) => c.providers.includes(slot.provider))
    .sort((a, b) => b.rating - a.rating);
}

export function countSlotsForCasino(casino: Casino): number {
  return slots.filter((s) => casino.providers.includes(s.provider)).length;
}

export function buildAffiliateUrl(casino: Casino, slot?: Slot): string {
  const base = `https://go.slotgids.co.nz/${casino.id}`;
  return slot ? `${base}?slot=${slot.slug}` : base;
}

export function getDemoUrl(slot: Slot): string | undefined {
  if (!slot.demoSymbol) return undefined;
  return `https://demogamesfree.pragmaticplay.net/gs2c/openGame.do?gameSymbol=${slot.demoSymbol}&websiteUrl=https%3A%2F%2Fdemogamesfree.pragmaticplay.net&jurisdiction=99&lobby_url=&lang=NL&cur=NZD`;
}

export function formatRtp(rtp: number): string {
  return `${rtp.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export function formatNzd(value: number): string {
  return `NZ$${value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function getProsCons(slot: Slot): { pros: string[]; cons: string[] } {
  const pros: string[] = [];
  const cons: string[] = [];

  if (slot.rtp >= 96.5) pros.push(`Bovengemiddelde RTP van ${formatRtp(slot.rtp)}`);
  if (slot.volatility === 'Laag' || slot.volatility === 'Gemiddeld') {
    pros.push('Regelmatige winsten, geschikt voor langere sessies');
  } else {
    pros.push(`Groot winpotentieel tot ${slot.maxWin} je inzet`);
  }
  pros.push(
    slot.release >= 2022
      ? 'Moderne mechanieken en sterke mobiele weergave'
      : 'Bewezen klassieker met jarenlange populariteit',
  );
  pros.push(slot.features[0]);

  if (slot.rtp < 96) cons.push(`RTP onder het marktgemiddelde van 96%`);
  if (slot.volatility === 'Hoog') cons.push('Lange droge periodes zijn mogelijk');
  if (slot.volatility === 'Zeer hoog') cons.push('Extreem volatiel; alleen met een strak budget');
  if (slot.volatility === 'Laag') cons.push(`Beperkte maximale winst (${slot.maxWin})`);
  if (slot.volatility === 'Gemiddeld') cons.push('Minder spektakel dan moderne high-volatility slots');
  cons.push('RTP kan per casino lager zijn ingesteld');

  return { pros, cons };
}

export function getProviderBySlug(slug: string): Provider | undefined {
  return providers.find((p) => p.slug === slug);
}

export function getProviderSlug(name: string): string {
  return providers.find((p) => p.name === name)?.slug ?? '';
}

export function getSlotsByProvider(name: string): Slot[] {
  return slots.filter((s) => s.provider === name);
}

export function getCasinosForProvider(name: string): Casino[] {
  return casinos.filter((c) => c.providers.includes(name)).sort((a, b) => b.rating - a.rating);
}

export function getProviderStats(name: string) {
  const list = getSlotsByProvider(name);
  const avg = (fn: (s: Slot) => number) => (list.length ? list.reduce((t, s) => t + fn(s), 0) / list.length : 0);
  return {
    count: list.length,
    avgRtp: avg((s) => s.rtp),
    avgScore: avg((s) => s.score),
    bestRank: list.length ? Math.min(...list.map((s) => s.rank)) : 0,
  };
}

export function getProviderSummary(): { name: string; count: number; top: Slot }[] {
  const map = new Map<string, Slot[]>();
  slots.forEach((s) => map.set(s.provider, [...(map.get(s.provider) ?? []), s]));
  return Array.from(map.entries())
    .map(([name, list]) => ({ name, count: list.length, top: list[0] }))
    .sort((a, b) => b.count - a.count || a.top.rank - b.top.rank);
}
